import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import { useEffect, useRef, useState } from 'react';

import type { NavigationMode } from './sharedTypes';

export const getCurrentPath = (asPath: string) => {
  const [pathWithNoQuery] = asPath.split('?');
  const [path] = pathWithNoQuery.split('#');

  return path;
};

export const getParsedQueryFromAsPath = (asPath: string): ParsedUrlQuery => {
  const [, queryStringWithHash = ''] = asPath.split('?');
  const [queryString] = queryStringWithHash.split('#');
  const params = new URLSearchParams(queryString);
  const parsedQuery: ParsedUrlQuery = {};

  params.forEach((value, key) => {
    const currentValue = parsedQuery[key];

    if (typeof currentValue === 'string') {
      parsedQuery[key] = [currentValue, value];
      return;
    }

    if (Array.isArray(currentValue)) {
      parsedQuery[key] = [...currentValue, value];
      return;
    }

    parsedQuery[key] = value;
  });

  return parsedQuery;
};

export const getQueryValues = (value: string | string[] | undefined) => {
  if (!value) return [];
  return (Array.isArray(value) ? value : [value]).filter(Boolean);
};

export const hasQueryParam = (query: ParsedUrlQuery, key: string) =>
  Object.prototype.hasOwnProperty.call(query, key);

export const sanitizeTakeValue = (value: string | undefined, pageSize: number) => {
  const take = Number(value);

  if (!Number.isInteger(take) || take < pageSize) {
    return pageSize;
  }

  return take;
};

type UseSearchPageUrlSyncOptions<UrlState> = {
  readUrlStateFromAsPath: (asPath: string) => UrlState;
  applyUrlState: (urlState: UrlState) => void;
  getCurrentUrlState: () => UrlState;
  serializeUrlState: (urlState: UrlState) => string;
  buildSearchParams: (urlState: UrlState) => URLSearchParams;
};

type UseSearchPageUrlSyncResult = {
  hasHydratedFromUrl: boolean;
  setNavigationMode: (mode: NavigationMode) => void;
};

export const useSearchPageUrlSync = <UrlState>({
  readUrlStateFromAsPath,
  applyUrlState,
  getCurrentUrlState,
  serializeUrlState,
  buildSearchParams,
}: UseSearchPageUrlSyncOptions<UrlState>): UseSearchPageUrlSyncResult => {
  const router = useRouter();
  const [hasHydratedFromUrl, setHasHydratedFromUrl] = useState(false);
  const pendingNavigationModeRef = useRef<NavigationMode>('replace');
  const lastUrlStateRef = useRef('');

  const syncUrlStateFromAsPath = (asPath: string) => {
    const nextUrlState = readUrlStateFromAsPath(asPath);

    applyUrlState(nextUrlState);
    lastUrlStateRef.current = serializeUrlState(nextUrlState);
  };

  useEffect(() => {
    if (!router.isReady || hasHydratedFromUrl) {
      return;
    }

    syncUrlStateFromAsPath(router.asPath);
    setHasHydratedFromUrl(true);
  }, [
    applyUrlState,
    hasHydratedFromUrl,
    readUrlStateFromAsPath,
    router.asPath,
    router.isReady,
    serializeUrlState,
  ]);

  useEffect(() => {
    if (!hasHydratedFromUrl || typeof window === 'undefined') {
      return;
    }

    const handlePopState = () => {
      const browserAsPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      syncUrlStateFromAsPath(browserAsPath);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [applyUrlState, hasHydratedFromUrl, readUrlStateFromAsPath, serializeUrlState]);

  useEffect(() => {
    if (!router.isReady || !hasHydratedFromUrl) {
      return;
    }

    const nextUrlState = getCurrentUrlState();
    const serializedUrlState = serializeUrlState(nextUrlState);

    if (serializedUrlState === lastUrlStateRef.current) {
      return;
    }

    const params = buildSearchParams(nextUrlState);
    const nextUrl = params.toString()
      ? `${getCurrentPath(router.asPath)}?${params.toString()}`
      : getCurrentPath(router.asPath);

    lastUrlStateRef.current = serializedUrlState;

    if (pendingNavigationModeRef.current === 'push') {
      void router.push(nextUrl, undefined, { shallow: true, scroll: false });
    } else {
      void router.replace(nextUrl, undefined, { shallow: true, scroll: false });
    }

    pendingNavigationModeRef.current = 'replace';
  }, [buildSearchParams, getCurrentUrlState, hasHydratedFromUrl, router, serializeUrlState]);

  return {
    hasHydratedFromUrl,
    setNavigationMode: (mode) => {
      pendingNavigationModeRef.current = mode;
    },
  };
};
